import { CARD_ITEM_WIDTH, FORM_FIELD_WIDTH } from '@/constants/dimensions';
import useProviderById from '@/hooks/provider/useProviderById';
import DirectoryProfileViewPageTemplate from '@/modules/directory/DirectoryProfileViewPageTemplate';
import RequestAProviderSessionModalTrigger from '@/modules/user/RequestATherapySessionModalTrigger';
import { extra, primary } from '@/theme/colors/palettes';
import { displayCapitalizedUnderscoredString } from '@/utils/stringUtils';
import { Text, VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';

const ProviderProfileView = () => {
  const { providerId } = Route.useParams();

  const { data: provider, isLoading: isLoadingProvider } = useProviderById(providerId);
  // TODO: make this logic work once we want to implement Provider-booking via this FE
  // const [startDate, setStartDate] = useState('');
  // const [endDate, setEndDate] = useState('');
  // const [dayOfWeek, setDayOfWeek] = useState(6);
  // const [days, setDays] = useState(5);

  // const {
  //   data: providerTimeSlots,
  //   isLoading: isLoadingProviderTimeSlots,
  //   refetch: refetchProviderTimeSlots,
  // } = useProviderTimeSlotsById(providerId, {
  //   startDate,
  //   endDate,
  //   dayOfWeek,
  //   days,
  // });

  const locationsFormatted =
    provider?.locations
      ?.map((location) => `${location.city}, ${location.state} ${location.zip}, United States`)
      .join('\n') ?? null;

  return (
    <DirectoryProfileViewPageTemplate
      isLoading={isLoadingProvider}
      name={`${provider?.first_name} ${provider?.last_name}`}
      profileImage={provider?.profile_photo}
      role={provider?.designation.map(displayCapitalizedUnderscoredString).join(', ')}
      roleColor="text.darkBlue"
      description={provider?.modalities ? provider?.modalities.join(', ') : 'No Modalities'}
    >
      {provider && (
        <VStack gap={'20px'}>
          <VStack marginY={'30px'}>
            <RequestAProviderSessionModalTrigger
              triggerButtonWidth={FORM_FIELD_WIDTH}
              backgroundColor={primary[500]}
              labelColor={extra.white}
              hoveredLabelColor="white"
              defaultIconColor="white"
              provider={{
                name: `${provider?.first_name} ${provider?.last_name}`,
                id: provider._id,
                designation: provider.designation,
                profilePhoto: provider.profile_photo ?? '',
              }}
            />
          </VStack>

          <SectionContainer
            title="Specialization"
            description={provider?.specialities.map(displayCapitalizedUnderscoredString).join(', ')}
          />

          <SectionContainer title="About Me" description={provider.bio ?? 'No About Me'} />

          <SectionContainer title="Population" description={provider?.population ?? 'No Population'} />

          {locationsFormatted && (
            <SectionContainer
              title={`In-Person Location${provider?.locations && provider?.locations.length > 1 ? 's' : ''}`}
              description={locationsFormatted}
            />
          )}
        </VStack>
      )}
    </DirectoryProfileViewPageTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_directory/_provider/directory/provider/$providerId')({
  staticData: { title: 'Provider' },
  component: ProviderProfileView,
});

const SectionContainer = ({ title, description }: { title: string; description: string }) => {
  return (
    <VStack
      backgroundColor={'white'}
      padding={'34px'}
      borderRadius={'12px'}
      alignItems={'flex-start'}
      minWidth={'500px'}
      width={CARD_ITEM_WIDTH}
      boxShadow={'0px 0px 20px 0px #00417926'}
    >
      <Text variant={'loraMedium22'} textAlign={'start'}>
        {title}
      </Text>
      <Text
        variant={'urbanistSemiBold'}
        lineHeight={'22px'}
        color={'text.mediumBlue'}
        marginTop={'10px'}
        whiteSpace={'pre-wrap'}
        textAlign={'start'}
      >
        {description}
      </Text>
    </VStack>
  );
};
